import styled from "@emotion/styled";
import React, { useState, useEffect, useContext } from "react";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import EditIcon from '@mui/icons-material/Edit';
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { __snackBarSeverity, __snackBarText } from "../../../constants";
import mainContext from "../../../contexts/mainContext";

function FormDialog({ open, handleClose, handleAdd, editData }) {
  const [state, setState] = useState({ from: 0, to: 0, sno: "", id: "" });

  useEffect(() => {
    if (editData) {
      setState({
        from: editData.from || 0,
        to: editData.to || 0,
        sno: editData.sno || "",
        id: editData._id || "",
      });
    } else {
      setState({ from: 0, to: 0, sno: "", id: "" });
    }
  }, [editData, open]);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{editData ? "Edit experience range" : "Add new experience range"}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="from"
          label="From"
          type="text"
          fullWidth
          variant="standard"
          value={state.from.toString().substring(0, 2)}
          onChange={(e) => {
            const reg = /^[0-9]*$/gi;
            if (reg.test(e.target.value.replace(/\n/g, " "))) {
              setState({ ...state, from: e.target.value });
            }
          }}
        />
        <TextField
          margin="dense"
          id="to"
          label="To"
          type="text"
          fullWidth
          variant="standard"
          value={state.to.toString().substring(0, 2)}
          onChange={(e) => {
            const reg = /^[0-9]*$/gi;
            if (reg.test(e.target.value.replace(/\n/g, " "))) {
              setState({ ...state, to: e.target.value });
            }
          }}
        />
        <TextField
          margin="dense"
          id="sno"
          label="Serial Number (1-1000)"
          type="text"
          fullWidth
          variant="standard"
          value={state.sno}
          helperText="Used for sorting and mobile display order"
          FormHelperTextProps={{ style: { color: "#999" } }}
          onChange={(e) => {
            const reg = /^[0-9]*$/gi;
            if (reg.test(e.target.value.replace(/\n/g, " "))) {
              setState({ ...state, sno: e.target.value });
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} size="small">Cancel</Button>
        <Button onClick={() => handleAdd(state)} variant="contained" size="small">
          {editData ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const ExperienceManager = ({
  navTitle,
  getExperienceController,
  addExperienceController,
  deleteExperienceController,
  permissionType,
}) => {
  const { setSnackBar, setProfileNavTitle } = useContext(mainContext);
  const [open, setOpen] = useState(false);
  const [experienceRanges, setExperienceRanges] = useState([]);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);

  const callSnackBar = (msg, type) => {
    setSnackBar({
      status: true,
      text: msg,
      severity: __snackBarSeverity[type],
    });
  };

  const handleClickOpen = () => {
    setEditData(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditData(null);
  };

  const handleEdit = (item) => {
    setEditData(item);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    const res = await deleteExperienceController(id);
    if (res && res.status === 500) {
      return callSnackBar(__snackBarText.internalServerError, "error");
    }
    if (res.data.status !== 200) {
      return callSnackBar(res.data.errors[0].message, "error");
    }
    await getExperienceRanges();
    setSnackBar({
      status: true,
      text: "Experience deleted successfully",
      severity: __snackBarSeverity.success,
    });
  };

  const handleAdd = async (state) => {
    const { from, to, sno, id } = state;
    if (from === "" || to === "" || sno === "") {
      return setSnackBar({
        status: true,
        text: __snackBarText.fieldEror,
        severity: __snackBarSeverity.error,
      });
    }
    if (from === to) {
      return setSnackBar({
        status: true,
        text: "The value of from/to are not equal.",
        severity: __snackBarSeverity.error,
      });
    }
    if (Number.parseInt(sno) < 1 || Number.parseInt(sno) > 1000) {
      return setSnackBar({
        status: true,
        text: "Serial number must be between 1 and 1000",
        severity: __snackBarSeverity.error,
      });
    }
    const res = await addExperienceController(from, to, permissionType, sno, id);
    if (res && res.status === 500) {
      return setSnackBar({
        status: true,
        text: __snackBarText.internalServerError,
        severity: __snackBarSeverity.error,
      });
    }
    if (res.data.status !== 200) {
      return setSnackBar({
        status: true,
        text: res.data.errors ? res.data.errors[0].message : res.data.message,
        severity: __snackBarSeverity.error,
      });
    }
    await getExperienceRanges();
    setSnackBar({
      status: true,
      text: id ? "Experience updated successfully" : "Experience added successfully",
      severity: __snackBarSeverity.success,
    });
    setOpen(false);
    setEditData(null);
  };

  const getExperienceRanges = async () => {
    setLoading(true);
    try {
      const res = await getExperienceController();
      if (res && res.status === 500) {
        setSnackBar({
          status: true,
          text: __snackBarText.internalServerError,
          severity: __snackBarSeverity.error,
        });
        return;
      }
      if (res.data.status !== 200) {
        setSnackBar({
          status: true,
          text: res.data.message,
          severity: __snackBarSeverity.error,
        });
        return;
      }
      if (res.data.data.experience.length === 0) {
        setSnackBar({
          status: true,
          text: __snackBarText.noDataFound,
          severity: __snackBarSeverity.info,
        });
      }
      setExperienceRanges(res.data.data.experience);
    } catch (error) {
      setSnackBar({
        status: true,
        text: "Error loading experience data",
        severity: __snackBarSeverity.error,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getExperienceRanges();
    setProfileNavTitle(navTitle);
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <FormDialog open={open} handleClose={handleClose} handleAdd={handleAdd} editData={editData} />
      <Conatiner>
        <Header>
          <Button variant="contained" size="small" onClick={handleClickOpen}>
            Add new
          </Button>
        </Header>
        <Body>
          {loading ? (
            <div className="loading-container">
              <p>Loading...</p>
            </div>
          ) : (
            <>
              <List>
                <div className="header">S.No</div>
                <div className="header">Experience</div>
                <div></div>
                <div></div>
              </List>
              <Divider className="divider-gap" />
              {experienceRanges.map((item) => (
                <List key={item._id}>
                  <div>{item.sno || "-"}</div>
                  <div>
                    {item.from} - {item.to ? item.to : "+"} <small style={{ marginLeft: "0.3rem" }}>years</small>
                  </div>
                  <div>
                    <IconButton onClick={() => handleEdit(item)}>
                      <EditIcon color="primary" fontSize="small" />
                    </IconButton>
                  </div>
                  <div>
                    <Button size="small" color="error" onClick={() => handleDelete(item._id)}>
                      Delete
                    </Button>
                  </div>
                </List>
              ))}
            </>
          )}
        </Body>
      </Conatiner>
    </>
  );
};

const Conatiner = styled("div")`
  .divider-gap {
    margin: 0.5rem 0;
  }
  button {
    text-transform: none;
  }
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
  }
`;

const Header = styled("div")`
  button {
    border-radius: 5px;
  }
`;
const Body = styled("div")`
  height: 80vh;
`;

const List = styled("div")`
.header{
    font-family: "semi-bold";
    font-size : 90%
}
  margin-top: 1rem;
  display: flex;
  align-items: center;
  &>div:nth-child(1){
    width: 5%;
  }
  &>div:nth-child(2){
    width: 75%;
  }
  &>div:nth-child(3){
    width: 10%;
  }
  &>div:nth-child(4){
    width: 10%;
  }
`;

const IconButton = styled("div")`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

export default ExperienceManager;